// Simple event-based system for managing temporary projects
import { v4 as uuid } from "uuid";

export interface TemporaryProject {
  id: string;
  ticker: string;
  exchange: string;
  companyName?: string;
  companyDescription?: string;
  companyWebsite?: string;
  companyStockType?: string;
  securityType?: string;
  companyLogoUrl?: string;
  createdAt: number;
}

type Listener = (projects: TemporaryProject[]) => void;

class TemporaryProjectManager {
  private temporaryProjects: TemporaryProject[] = [];
  private listeners: Listener[] = [];

  // Minimum time (in ms) that a temporary project should be visible
  private readonly MIN_DISPLAY_TIME = 3000; // 3 seconds

  // Add a temporary project
  addTemporaryProject(project: Omit<TemporaryProject, "id" | "createdAt">): string {
    const id = uuid();
    const newProject = {
      ...project,
      id,
      createdAt: Date.now(),
    };
    this.temporaryProjects.push(newProject);
    this.notifyListeners();
    return id;
  }

  // Remove a temporary project by ID
  removeTemporaryProject(id: string): void {
    const project = this.temporaryProjects.find((p) => p.id === id);
    if (!project) return;

    const timeElapsed = Date.now() - project.createdAt;

    if (timeElapsed < this.MIN_DISPLAY_TIME) {
      // If the project hasn't been displayed for the minimum time,
      // schedule its removal for later
      console.log(`Delaying removal of temporary project ${id} for ${this.MIN_DISPLAY_TIME - timeElapsed}ms`);
      setTimeout(() => {
        this.temporaryProjects = this.temporaryProjects.filter((p) => p.id !== id);
        this.notifyListeners();
      }, this.MIN_DISPLAY_TIME - timeElapsed);
    } else {
      // Remove immediately if it's been displayed long enough
      this.temporaryProjects = this.temporaryProjects.filter((p) => p.id !== id);
      this.notifyListeners();
    }
  }

  // Get all temporary projects
  getTemporaryProjects(): TemporaryProject[] {
    return [...this.temporaryProjects];
  }

  // Subscribe to changes
  subscribe(listener: Listener): () => void {
    this.listeners.push(listener);

    // Return unsubscribe function
    return () => {
      this.listeners = this.listeners.filter((l) => l !== listener);
    };
  }

  // Notify all listeners of changes
  private notifyListeners(): void {
    const projects = this.getTemporaryProjects();
    this.listeners.forEach((listener) => listener(projects));
  }
}

// Create a singleton instance
export const temporaryProjectManager = new TemporaryProjectManager();
